import {
    CloseCircleOutlined,
    DeleteOutlined,
    EditFilled,
    MoreOutlined,
    SaveFilled,
  } from "@ant-design/icons";
  import {
    Box,
    Card,
    CardMedia,
    Dialog,
    DialogContent,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Typography,
    Button,
  } from "@mui/material";
  import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
  import { DatePicker } from "@mui/x-date-pickers/DatePicker";
  import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
  import dayjs from "dayjs";
  import { useEffect, useRef, useState } from "react";
  import { useNotification } from "../../../hooks/useNotification.js";
  import { JournalAPIService } from "../../../services/api/JournalAPIService";
  import {
    API_ERROR_MESSAGE,
    API_SUCCESS_MESSAGE,
    DATE,
  } from "../../../shared/constants";
  import ConfirmationDialog from "../../common/ConfirmationDialog.js";
  import magicToolIcon from "../../../assests/Images/magicTool.png";
  import ActionMenu from "../../../pages/Extra/ActionMenu.js";
  import html2canvas from "html2canvas";
  import DownloadBackdrop from "../../../pages/Memory_journal/DownloadBackdrop.js";
import { FFmpeg } from "@ffmpeg/ffmpeg";

  const ffmpeg = new FFmpeg();

  const VideoCardTemplateEdit = ({
    journalId,
    author,
    imgSrc,
    text,
    date,
    borderColor = "#f72bb1",
    backgroundColor = "#007bff",
    imageBottomRight,
    imageBottomRightStyle,
    textColor,
    disableEye = false,
    editData = false,
    open,
    onClose,
    pageRefreshCallback,
    handleOpenEditor,
  }) => {
    const [showEye, setShowEye] = useState(false);
    const { notifyError, notifySuccess } = useNotification();
    const [editMode, setEditOpen] = useState(editData);
    const [editableDate, setEditableDate] = useState(dayjs(date));
    const [editableText, setEditableText] = useState(text);
    const [anchorEl, setAnchorEl] = useState(null);
    const textRef = useRef(null);
    const [isSingleLine, setIsSingleLine] = useState(true);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const cardRef = useRef(null);
    const [downloading, setDownloading] = useState(false);
    const openMenu = Boolean(anchorEl);

    const handleMenuClick = (action) => {
      switch (action) {
        case "edit":
          handleEdit();
          break;
        case "delete":
          handleDelete();
          break;
        case "effect":
          handleOpenEditor(imgSrc, journalId);
          break;
        case "save":
          handleDownloadVideo();
          break;
        case "drag":

          console.log("Drag action triggered");
          // Optional: set some dragging state here
          break;
        default:
          break;
      }
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
      setShowEye(false);
    };

    const handleCloseDialog = () => {
      onClose();
      setEditOpen(false);
      setShowEye(false);
    };

    const handleEdit = () => {
      setShowEye(false);
      setEditOpen(true);
    };

    const handleCancelDelete = () => {
      setConfirmDialogOpen(false);
    };

    const handleDelete = () => {
      setConfirmDialogOpen(true);
    };

    const handleConfirmDelete = () => {
      setConfirmDialogOpen(false);

      JournalAPIService.journalDelete(journalId)
        .then((response) => {
          notifySuccess(
            response.message || API_SUCCESS_MESSAGE.DELETED_SUCCESSFULLY
          );
          pageRefreshCallback();
        })
        .catch((errResponse) => {
          notifyError(
            errResponse?.response?.data?.message ||
              API_ERROR_MESSAGE.INTERNAL_SERVER_ERROR
          );
        });
      handleCloseDialog();
    };

    const handleDownloadImage = async () => {
      if (!cardRef.current) return;

      try {
        setDownloading(true);

        const canvas = await html2canvas(cardRef.current, {
          useCORS: true,
          backgroundColor: null,
          // scale: 8,
          allowTaint: true,
          logging: false,
          removeContainer: true,
          imageTimeout: 15000,
        });

        const hashCode = (str) => {
          let hash = 0;
          for (let i = 0; i < str.length; i++) {
            hash = (hash << 5) - hash + str.charCodeAt(i);
            hash |= 0;
          }
          return Math.abs(hash % 10000);
        };

        const shortCode = hashCode(journalId);

        const dataUrl = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `m${shortCode}.png`;
        link.click();
      } catch (error) {
        console.error("Download failed:", error);
      } finally {
        setDownloading(false);
      }
    };

    const handleUpdate = () => {
      JournalAPIService.journalUpdate(journalId, {
        posted_at: dayjs(editableDate).toISOString(),
        tagline: editableText,
        author,
      })
        .then((response) => {
          notifySuccess(API_SUCCESS_MESSAGE.UPDATED_SCUCESSFULLY);
          pageRefreshCallback();
        })
        .catch((errResponse) => {
          notifyError(
            errResponse?.response?.data?.message ||
              API_ERROR_MESSAGE.INTERNAL_SERVER_ERROR
          );
        });
      handleCloseDialog();
    };

    useEffect(() => {
      const element = textRef.current;
      if (element) {
        const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
        const height = element.clientHeight;

        // If height is more than 1.5x the lineHeight, we assume it's multiline
        if (height > lineHeight * 1.2) {
          setIsSingleLine(false);
        } else {
          setIsSingleLine(true);
        }
      }
    }, [text]);

    const isVideo =
      /\.(mp4|webm|ogg|m4v|mov|avi|mkv|3gpp|3gp|3g2|hevc|h265)$/i.test(imgSrc);

      const generateVideoWithOverlay = async (backgroundVideo) => {

        try {
          if (!ffmpeg.loaded) await ffmpeg.load();

          const bgVideoArrayBuffer = await fetch(URL.createObjectURL(backgroundVideo)).then(res => res.arrayBuffer());

          await ffmpeg.writeFile("background.mp4", new Uint8Array(bgVideoArrayBuffer));

          // FFmpeg: overlay PNG onto video
          await ffmpeg.exec([
            "-i", "background.mp4",
            "-filter_complex",
            "scale=520:720:flags=lanczos",
            "-c:v", "libx264",
            "-pix_fmt", "yuv420p",
            "-preset", "ultrafast",
            "output_overlay.mp4"
          ]);
          const data = await ffmpeg.readFile("output_overlay.mp4");
          const url = URL.createObjectURL(new Blob([data.buffer], { type: "video/mp4" }));
          // Auto-download the file
        const a = document.createElement("a");
        a.href = url;
        a.download = "Joy_Memory.mp4";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Optional: Revoke the URL after some time to free memory
        setTimeout(() => URL.revokeObjectURL(url), 1000);

          // setVideoUrl(url);
        } catch (error) {
          console.error("Error generating overlay video:", error);
        }
      };


      const handleDownloadVideo = async () => {
        if (!cardRef.current) return;

        try {
          setDownloading(true);

          // Use your own video path here
          const backgroundVideo = await fetch(imgSrc).then(res => res.blob());; // put this in your public folder

          await generateVideoWithOverlay(backgroundVideo);
        } catch (err) {
          console.error("Video generation failed:", err);
          notifyError("Video generation failed.");
        } finally {
          setDownloading(false);
        }
      };

    return (
      <>
        {/* Dialog */}
        <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogContent
            sx={{
              position: "relative",
              p: 2,
              height: { xs: "380px", sm: "400px", md: "400px" },
            }}
          >
            {editMode ? (
              <Button
                variant="contained"
                onClick={handleUpdate}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  zIndex: 10000,
                  marginTop: "2px",
                  // marginBottom: "1px",
                  // margin: "0 16px 0 0",
                  fontSize: "0.5rem",
                  color: "#ffff",
                  background: "#005cff",
                  fontFamily: "Poppins",
                  textTransform: "none",
                  // borderRadius: "24px",
                }}
              >
                Save and Close
              </Button>
            ) : (
              <IconButton
                onClick={handleCloseDialog}
                sx={{ position: "absolute", top: 2, right: 2, zIndex: 10000 }}
              >
                <CloseCircleOutlined />
              </IconButton>
            )}

  <IconButton
                  sx={{
                    position: "absolute",
                    top: 60,
                    right: 150,
                    color: "black",
                    zIndex: 1001,
                    width: { xs: "25px", sm: "30px", md: "30px" },
                    height: { xs: "25px", sm: "30px", md: "30px" },
                    "@media (max-width: 440px)": {
                      height: "20px",
                      width: "20px",
                      top:60,
                      right:40
                    },
                    "@media (min-width: 441px) and (max-width: 600px)": {
                      height: "24px",
                      width: "24px",
                      top: 80,
                      right: 50,
                    }
                    // bgcolor: "#ffffffc9",
                  }}
                  // onClick={handleMenuClick}
                >
                  <ActionMenu action={handleMenuClick} show={isVideo} edit={true} drag={false} fullView={false}/>
                </IconButton>
          <div ref={cardRef} style={{width:"97%",padding: "4px",}}>
            <Card
              sx={{
                width: {
                  xs: disableEye ? "96%" : "100%",
                  sm: disableEye ? "60%" : "100%",
                  md: disableEye ? "60%" : "100%",
                },
                height: {
                  xs: disableEye ? (editMode ? 340 : 300) : 250,
                  sm: disableEye ? 375 : 340,
                  md: disableEye ? 375 : 340,
                },
                "@media (max-width: 370px)": {
                  height: disableEye ? (editMode ? 340 : 300) : 250,
                },
                borderRadius: 4,
                marginTop: {
                  xs: disableEye ? "10%" : "0%",
                  sm: disableEye ? "4%" : "0%",
                  md: disableEye ? "4%" : "0%",
                },
                border: `4px solid ${borderColor}`,
                position: "relative",
                overflow: "visible",
                justifySelf: "center",
                bgcolor: backgroundColor || "#f9f9f9",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={() => setShowEye(true)}
                onMouseLeave={() => setShowEye(false)}
              >

                <CardMedia
                  component={isVideo ? "video" : "img"}
                  image={imgSrc}
                  controls
                  muted
                  loop
                  alt="Uploaded"
                  sx={{
                    objectFit: "inherit",
                    width: "100%",
                    height: {
                        xs: disableEye ? (editMode ? 340 : 300) : 250,
                        sm: disableEye ? 375 : 340,
                        md: disableEye ? 375 : 340,
                      },
                      "@media (max-width: 370px)": {
                        height: disableEye ? (editMode ? 340 : 300) : 250,
                      },
                    borderRadius: "12px",
                  }}
                />
              </Box>


            </Card>
            </div>

            <ConfirmationDialog
              id="confirmation-dialog-description"
              open={confirmDialogOpen}
              title=""
              description="Are you sure you want to delete this memory?"
              onConfirm={handleConfirmDelete}
              onCancel={handleCancelDelete}
              confirmText="Delete"
              cancelText="Cancel"
              keepMounted={false}
              transition="up"
              loading={false}
            />
          </DialogContent>
        </Dialog>
        <DownloadBackdrop downloading={downloading} />
      </>
    );
  };

  export default VideoCardTemplateEdit;
